import React from 'react';
import classnames from 'classnames';
import { prettyMoney } from '@scramble/utils';
import { useTranslation } from 'react-i18next';
import { useWindowSize } from '@/hooks/useWindowSize';
import { EMPTY_DASH } from '@/helpers/constants';
import { useGetFundraisingStats } from '@/query/queries/investing/useGetFundraisingStats';
import s from './index.module.scss';

export const InvestingProgressBar = () => {
  const { data, isLoading } = useGetFundraisingStats();
  const current = data?.currently_allocated || 0;
  const min = data?.min_goal || 0;
  const max = data?.total_goal || 0;

  const { t } = useTranslation('investing');
  const { isMobile } = useWindowSize();

  const result = React.useMemo(() => {
    const res = {
      currentPercent: 0,
      maxPercent: 100,
      minPercent: 100,
      currentLineWidth: 0,
      maxLineWidth: 100,
      minLineWidth: 100,
    };

    const minGoal = min || max;

    const maxPercent = Number((max * 100) / minGoal);

    res.maxPercent = maxPercent;

    if (max !== 0) {
      res.currentPercent = Number((current * maxPercent) / max);

      const _currentLineWidth = Number((current * 100) / max);
      res.currentLineWidth = _currentLineWidth > 100 ? 100 : _currentLineWidth;

      const _minLineWidth = Number((100 * 100) / maxPercent);
      res.minLineWidth = _minLineWidth > 100 ? 100 : _minLineWidth;
    }

    return res;
  }, [current, min, max]);

  const {
    currentPercent,
    maxPercent,
    minPercent,
    minLineWidth,
    maxLineWidth,
    currentLineWidth,
  } = result;

  return (
    <div className={s.wrapper}>
      <div className={s.title}>
        {t('investing:progress_bar.investing_goal')}
      </div>
      <div className={s.stats}>
        <div className={s.stats_left}>
          <div className={s.stats_name}>
            {t('investing:progress_bar.current')}
          </div>
          <div className={s.stats_value}>
            {isLoading ? EMPTY_DASH : prettyMoney(current)}
          </div>
        </div>
        <div className={s.stats_right}>
          <div className={s.stats_name}>{t('investing:progress_bar.max')}</div>
          <div className={s.stats_value}>
            {isLoading ? EMPTY_DASH : prettyMoney(max || min)}
          </div>
        </div>
      </div>
      <div className={s.bar_wrapper}>
        <div className={s.bar}>
          <div
            className={classnames(s.line, s.line_max)}
            style={{ width: `${maxLineWidth}%` }}
          >
            <span className="f-body-md">{parseInt(maxPercent || 100)}%</span>
          </div>

          <div
            className={classnames(s.line, s.line_current)}
            style={{ zIndex: current > min ? 1 : 2 }}
          >
            <div
              className={s.line_current_inner}
              style={{
                background: current > (min || max) ? '#F8A100' : '#FFD200',
                width: `max(${currentLineWidth}%, ${isMobile ? '48px' : '57px'})`,
              }}
            />
          </div>

          {!!min && (
            <div
              className={classnames(s.line, s.line_min)}
              style={{
                width: `${minLineWidth}%`,
                background: current > min ? '#FFD200' : '#FFF1B2',
                zIndex: current > min ? 2 : 1,
              }}
            >
              <span className="f-body-md">{parseInt(minPercent)}%</span>
            </div>
          )}

          <div
            className={classnames(s.line, s.line_transparent)}
            style={{
              width: `max(${currentLineWidth}%, ${isMobile ? '48px' : '57px'})`,
            }}
          >
            <div className={s.pointer_wrap}>
              <div className={s.pointer}>{parseInt(currentPercent || 0)}%</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
